import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowDown, CheckCircle2, Loader2, ShieldCheck, Check, MoonStar,
  Phone, FileText, Pill, Truck, Home, Clock, Award, Route, HeartHandshake,
  MapPin, HeartPulse, Users, Stethoscope, RefreshCw, Mail,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import PrivacyLink from "../components/PrivacyLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#0B192C] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#10B981] focus:bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const HIGHLIGHTS = [
  { icon: Award, color: "#10B981", label: "Over 5 Years of Service" },
  { icon: Clock, color: "#06B6D4", label: "9 pm – 8 am On-Call Cover" },
  { icon: HeartPulse, color: "#EF4444", label: "Supporting Palliative Care Patients" },
  { icon: MapPin, color: "#F59E0B", label: "Serving the Wigan Area" },
];

const STEPS = [
  { icon: Phone, title: "GP Telephone Request",
    text: "An authorised GP or referring healthcare professional contacts the NCS out-of-hours team regarding a prescription requirement for a palliative care patient. The request is received and the relevant collection and delivery arrangements are confirmed." },
  { icon: FileText, title: "Prescription Collection",
    text: "Our team arranges collection of the prescription from the agreed location, following the applicable prescription handling and verification procedures." },
  { icon: Pill, title: "Pharmacy Dispensing",
    text: "The prescription is processed and the medication is dispensed by an appropriately authorised pharmacy, with the required professional and safety checks." },
  { icon: Truck, title: "Medication Delivery",
    text: "Following dispensing and handover, the medication is transported to the patient's nominated address, following appropriate medicines handling, security and transport procedures." },
  { icon: Home, title: "Patient Receives Medication",
    text: "The prescribed medication is delivered to the patient or appropriate recipient, supporting timely access to treatment and continuity of palliative care." },
];

const EXPERIENCE = [
  { icon: Award, color: "#10B981", title: "Established Experience",
    text: "More than five years supporting out-of-hours medication arrangements." },
  { icon: MoonStar, color: "#06B6D4", title: "Overnight Availability",
    text: "An on-call service operating from 9 pm to 8 am." },
  { icon: Route, color: "#F59E0B", title: "Coordinated Delivery",
    text: "A structured pathway connecting healthcare professionals, pharmacy dispensing and patient delivery." },
  { icon: HeartHandshake, color: "#EF4444", title: "Patient-Centred Approach",
    text: "Supporting access to prescribed medicines for patients receiving palliative care at home." },
];

const WHY = [
  { icon: Clock, color: "#10B981", title: "Timely Access to Medicines",
    text: "Supporting patients in accessing prescribed medication overnight." },
  { icon: Users, color: "#06B6D4", title: "Supporting Patients and Families",
    text: "Helping coordinate medication supply for patients receiving palliative and end-of-life care at home." },
  { icon: Stethoscope, color: "#F59E0B", title: "Supporting Healthcare Professionals",
    text: "Providing a defined pathway for out-of-hours medication collection and delivery." },
  { icon: RefreshCw, color: "#EF4444", title: "Continuity of Care",
    text: "Helping reduce disruption to the prescribed treatment pathway outside normal working hours." },
];

const PARTNERSHIP_POINTS = [
  "Out-of-hours palliative care medication supply coordination",
  "Prescription collection and pharmacy liaison",
  "Medication delivery arrangements",
  "Defined overnight on-call coverage",
  "Service planning and operational mobilisation",
  "Pharmacy and healthcare provider partnerships",
  "Agreed reporting and performance monitoring",
  "Service continuity and escalation arrangements",
];

const SERVICE_TYPES = [
  "Palliative Care Out-of-Hours Medication Supply",
  "Prescription Collection & Delivery",
  "Pharmacy Partnership",
  "Out-of-Hours Service Commissioning",
  "Other",
];

/* Replaceable image: uses the admin-uploaded object-storage image when one is set, else the default asset */
const OohImage = ({ slot, fallback, alt, className, testid }) => {
  const [src, setSrc] = useState(fallback);
  useEffect(() => {
    axios.get(`${API}/out-of-hours/content`)
      .then((r) => { if (r.data?.[`${slot}_custom`]) setSrc(`${API}/out-of-hours/image/${slot}`); })
      .catch(() => {});
  }, [slot]);
  return (
    <img
      src={src}
      onError={() => { if (src !== fallback) setSrc(fallback); }}
      alt={alt}
      loading="lazy"
      data-testid={testid}
      className={className}
    />
  );
};

const OohEnquiryForm = () => {
  const empty = {
    full_name: "", job_title: "", organisation: "", email: "", phone: "",
    area: "", service: SERVICE_TYPES[0], hours: "", coverage: "",
    start_date: "", additional: "", website: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const details = [
      { label: "Job title", value: form.job_title },
      { label: "Geographical area", value: form.area },
      ...(form.hours ? [{ label: "Proposed hours of operation", value: form.hours }] : []),
      ...(form.coverage ? [{ label: "Estimated service coverage", value: form.coverage }] : []),
    ];
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.full_name,
        organisation: form.organisation,
        org_type: "Out of Hours / NHS Commissioning",
        email: form.email,
        phone: form.phone,
        requirement: `Out of Hours — ${form.service}`,
        start_date: form.start_date || null,
        additional: form.additional || null,
        journey: "out-of-hours",
        website: form.website,
        calculator: details,
      });
      setSent(true);
      toast.success("Enquiry submitted — our out-of-hours team will respond shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="ooh-enquiry-success" className="bg-white border border-[#10B981]/30 rounded-3xl p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Commissioning enquiry received</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.full_name.split(" ")[0]}. Your enquiry is with our out-of-hours team —
          we will contact you to discuss your requirements. A confirmation email is on its way to {form.email}.
        </p>
        <button data-testid="ooh-enquiry-new-button" onClick={() => { setSent(false); setForm(empty); }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors">
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="ooh-enquiry-form"
      className="bg-white border border-[#E2E8F0] rounded-3xl p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="ooh-name" className={labelCls}>Full name *</label>
          <input id="ooh-name" data-testid="ooh-input-name" required minLength={2}
            value={form.full_name} onChange={upd("full_name")} placeholder="Dr. Sarah Collins" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-job" className={labelCls}>Job title *</label>
          <input id="ooh-job" data-testid="ooh-input-jobtitle" required minLength={2}
            value={form.job_title} onChange={upd("job_title")} placeholder="Commissioning Manager" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-org" className={labelCls}>Organisation / ICB *</label>
          <input id="ooh-org" data-testid="ooh-input-organisation" required minLength={2}
            value={form.organisation} onChange={upd("organisation")} placeholder="NHS Greater Manchester ICB" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-email" className={labelCls}>NHS or work email *</label>
          <input id="ooh-email" data-testid="ooh-input-email" type="email" required
            value={form.email} onChange={upd("email")} placeholder="s.collins@nhs.net" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-phone" className={labelCls}>Contact telephone *</label>
          <input id="ooh-phone" data-testid="ooh-input-phone" type="tel" required minLength={5}
            value={form.phone} onChange={upd("phone")} placeholder="07123 456789" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-area" className={labelCls}>Geographical area *</label>
          <input id="ooh-area" data-testid="ooh-input-area" required minLength={2}
            value={form.area} onChange={upd("area")} placeholder="e.g. Bolton, Greater Manchester" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-service" className={labelCls}>Type of service required *</label>
          <select id="ooh-service" data-testid="ooh-select-service" value={form.service} onChange={upd("service")} className={inputCls}>
            {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ooh-hours" className={labelCls}>Proposed hours of operation</label>
          <input id="ooh-hours" data-testid="ooh-input-hours"
            value={form.hours} onChange={upd("hours")} placeholder="e.g. 9 pm – 8 am, 7 nights" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-coverage" className={labelCls}>Estimated service coverage</label>
          <input id="ooh-coverage" data-testid="ooh-input-coverage"
            value={form.coverage} onChange={upd("coverage")} placeholder="e.g. 300,000 population, 40 practices" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ooh-start" className={labelCls}>Preferred start date</label>
          <input id="ooh-start" data-testid="ooh-input-startdate" type="date"
            value={form.start_date} onChange={upd("start_date")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ooh-additional" className={labelCls}>Additional requirements</label>
          <textarea id="ooh-additional" data-testid="ooh-input-additional" rows={4}
            value={form.additional} onChange={upd("additional")}
            placeholder="Current arrangements, pharmacy partners, governance requirements, mobilisation timescales..."
            className={`${inputCls} resize-none`} />
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="ooh-web">Website</label>
          <input id="ooh-web" data-testid="ooh-honeypot" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={upd("website")} />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 bg-[#F1F5F9] rounded-2xl px-5 py-4">
        <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-[#475569]">
          Please do not include patient-identifiable or confidential clinical information. Your
          details are processed solely to respond to your enquiry. Any proposed service is subject to
          agreed specification, governance, commissioning and contractual approval. Read our <PrivacyLink testid="ooh-privacy-link" />.
        </p>
      </div>

      <button type="submit" disabled={sending} data-testid="ooh-submit-btn"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<>Submitting <Loader2 size={16} className="animate-spin" /></>)
          : (<>Submit Commissioning Enquiry <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
      </button>
    </form>
  );
};

const OutOfHours = () => (
  <main data-testid="ooh-page">
    <Seo
      title="NCS Out of Hours | Palliative Care Medication Supply, Wigan"
      description="NCS Out of Hours provides a dedicated overnight medication supply and delivery service for palliative care patients across the Wigan area, 9 pm to 8 am. Enquiries welcome from ICBs and NHS commissioners."
    />

    {/* HERO */}
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">
            <MoonStar size={14} /> Overnight medication supply & delivery — Wigan
          </p>
          <h1 data-testid="ooh-hero-heading"
            className="mt-4 font-display font-extrabold tracking-tight leading-[1.06] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>NCS Out of Hours</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="font-serif-accent">Supporting palliative care patients</span> when it matters most
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl">
            National Clinical Services provides a dedicated out-of-hours medication supply and delivery
            service for palliative care patients across the Wigan area.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.68 }}
            className="mt-4 text-base leading-relaxed text-[#475569] max-w-2xl">
            Operating from 9:00 pm until 8:00 am, our service supports healthcare professionals in
            arranging access to prescribed medication overnight, helping maintain continuity of care for
            patients receiving palliative and end-of-life support. With over five years of service
            delivery, NCS has developed experience in coordinating prescription collection, pharmacy
            dispensing and medication delivery to patients' homes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            {HIGHLIGHTS.map((hl, i) => (
              <span key={hl.label} data-testid={`ooh-highlight-${i + 1}`}
                className="inline-flex items-center gap-2.5 bg-white border border-[#E2E8F0] rounded-full px-4 py-3">
                <hl.icon size={16} style={{ color: hl.color }} />
                <span className="text-xs sm:text-sm font-semibold text-[#0B192C]">{hl.label}</span>
              </span>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.92 }}
            className="mt-8">
            <button onClick={() => scrollToId("ooh-enquiry")} data-testid="ooh-hero-cta"
              className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
              Discuss an Out-of-Hours Service Contract
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative">
            <img src="/assets/ncs-mark.png?v=2" alt="" aria-hidden="true"
              className="absolute -top-12 -left-12 w-40 opacity-20 -rotate-12 select-none pointer-events-none" />
            <div className="relative rounded-3xl overflow-hidden border-[6px] border-white shadow-[0_16px_40px_-12px_rgba(11,25,44,0.12)]">
              <OohImage slot="hero" fallback="/assets/ooh-hero.png" testid="ooh-hero-image"
                alt="A healthcare delivery professional handing medication to a patient at their front door at night"
                className="w-full h-[340px] sm:h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-sm border border-[#E2E8F0] px-5 py-3.5">
              <p className="font-display text-sm font-bold text-[#0B192C]">On call 9 pm – 8 am, every night.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ABOUT THE SERVICE */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
            <OohImage slot="about" fallback="/assets/ooh-about.png" testid="ooh-about-image"
              alt="A pharmacist carefully preparing prescription medicines in a pharmacy dispensary at night"
              className="w-full h-[300px] sm:h-[380px] object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 order-1 lg:order-2">
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">About our out-of-hours service</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
            A Trusted Out-of-Hours Medication Supply Service
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#475569]">
            <p>
              For patients receiving palliative and end-of-life care, timely access to prescribed
              medication is an important part of maintaining comfort, dignity and continuity of care.
            </p>
            <p>
              NCS Out of Hours provides a coordinated service supporting the collection, dispensing and
              delivery of prescribed medication outside normal pharmacy opening hours.
            </p>
            <p>
              Our service has operated in the Wigan area for over five years, supporting the established
              out-of-hours medication pathway associated with the Greater Manchester Medicines Management
              Group (GMMMG).
            </p>
            <p>
              We understand the importance of clear communication, dependable processes and timely
              medication access, particularly for patients and families receiving care at home. Our
              approach brings together healthcare professionals, pharmacy services and delivery
              coordination to support the safe supply of prescribed medication.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* PROCESS — visual centrepiece */}
    <section id="process" className="py-20 sm:py-28 lg:py-32 bg-[#0B192C] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal className="text-center">
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#C5A059]">How our service works</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
            From GP Request to Patient Delivery
          </h2>
          <p data-testid="ooh-flow-label"
            className="mt-5 inline-flex flex-wrap justify-center items-center gap-x-2 gap-y-1 text-xs sm:text-sm font-semibold text-white/60">
            GP Request <ArrowRight size={13} className="text-[#06B6D4]" />
            Prescription Collection <ArrowRight size={13} className="text-[#06B6D4]" />
            Pharmacy Dispensing <ArrowRight size={13} className="text-[#06B6D4]" />
            Medication Delivery <ArrowRight size={13} className="text-[#06B6D4]" />
            Patient Receipt
          </p>
        </Reveal>
        <div className="mt-14 flex flex-col lg:flex-row items-stretch justify-center gap-4 lg:gap-2">
          {STEPS.map((s, i) => (
            <Fragment key={s.title}>
              <Reveal delay={i * 0.1} className="flex-1">
                <div className="w-full h-full flex flex-col bg-white/[0.06] border border-white/10 rounded-3xl p-6 transition-colors duration-300 hover:bg-white/[0.10]"
                  data-testid={`ooh-step-${i + 1}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#06B6D4] text-[#0B192C] font-display font-extrabold text-sm flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <span className="w-11 h-11 rounded-2xl bg-[#06B6D4]/15 text-[#06B6D4] flex items-center justify-center shrink-0">
                      <s.icon size={20} />
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-base font-bold text-white leading-snug uppercase tracking-wide">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/60 flex-1">{s.text}</p>
                </div>
              </Reveal>
              {i < STEPS.length - 1 && (
                <>
                  <ArrowRight size={18} className="hidden lg:block text-[#06B6D4] self-center shrink-0" aria-hidden="true" />
                  <ArrowDown size={20} className="lg:hidden text-[#06B6D4] self-center shrink-0" aria-hidden="true" />
                </>
              )}
            </Fragment>
          ))}
        </div>
        <Reveal className="text-center mt-10">
          <p className="text-xs leading-relaxed text-white/40 max-w-2xl mx-auto">
            NCS coordinates the out-of-hours pathway. Dispensing is always undertaken by an appropriately
            authorised pharmacy, and every stage follows the applicable professional, security and
            medicines-handling requirements.
          </p>
        </Reveal>
      </div>
    </section>

    {/* SERVICE EXPERIENCE */}
    <section className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our service experience</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
            Over Five Years of Supporting Out-of-Hours Palliative Care
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#475569] max-w-3xl">
            <p>
              National Clinical Services has supported this specialist medication supply and delivery
              pathway for over five years. Our continued involvement reflects the importance of
              maintaining a dependable out-of-hours service for patients, healthcare professionals and
              the wider care system.
            </p>
            <p>
              Through practical operational experience, NCS understands the challenges involved in
              coordinating urgent medication requirements overnight, including communication, prescription
              collection, pharmacy liaison and safe delivery.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {EXPERIENCE.map((c, i) => (
            <Reveal key={c.title} delay={(i % 4) * 0.07}>
              <div data-testid={`ooh-experience-card-${i + 1}`}
                className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                <span className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${c.color}1A`, color: c.color }}>
                  <c.icon size={20} />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{c.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* COMMISSIONING EXPERIENCE — GMMMG */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our commissioning experience</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
            Supporting the Greater Manchester Medicines Management Pathway
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#475569]">
            <p>
              Our established out-of-hours work in the Wigan area has involved service provision
              associated with GMMMG (Greater Manchester Medicines Management Group).
            </p>
            <p>
              This experience has helped NCS develop a practical understanding of the coordination and
              operational requirements involved in out-of-hours palliative care medication supply.
            </p>
            <p>
              We welcome opportunities to discuss how similar service models could support other NHS
              commissioning areas.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-5">
          <div data-testid="ooh-gmmmg-card"
            className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-3xl p-8 text-center">
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Commissioning pathway experience</p>
            <p className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0B192C]">GMMMG</p>
            <p className="mt-2 text-sm font-semibold text-[#475569]">Greater Manchester Medicines Management Group</p>
            <p className="mt-4 text-xs leading-relaxed text-[#64748B]">
              Associated out-of-hours medicines pathway — Wigan area. Pathway association shown for
              experience reference only; no organisational endorsement is implied.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* WHY IT MATTERS */}
    <section className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Why it matters</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
            Supporting Continuity of Care Beyond Normal Working Hours
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY.map((c, i) => (
            <Reveal key={c.title} delay={(i % 4) * 0.07}>
              <div data-testid={`ooh-why-card-${i + 1}`}
                className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                <span className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${c.color}1A`, color: c.color }}>
                  <c.icon size={20} />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{c.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* EXPANSION & PARTNERSHIPS */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Service expansion & NHS partnerships</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
            Commission an Out-of-Hours Medication Supply Service
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#475569] max-w-3xl">
            <p>
              National Clinical Services welcomes enquiries from Integrated Care Boards, NHS trusts,
              primary care organisations, out-of-hours providers and healthcare commissioners interested
              in developing or commissioning out-of-hours medication supply and delivery pathways.
            </p>
            <p>
              Drawing on our experience supporting palliative care patients in the Wigan area, we can
              discuss the operational requirements for similar services in other geographical locations.
              Potential partnership discussions may include:
            </p>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 max-w-4xl">
          {PARTNERSHIP_POINTS.map((p) => (
            <Reveal key={p}>
              <span className="flex items-start gap-2.5 text-sm text-[#0B192C]">
                <Check size={16} className="text-[#10B981] shrink-0 mt-0.5" /> {p}
              </span>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <p className="text-xs leading-relaxed text-[#64748B] max-w-3xl">
            All proposed services would be subject to agreed specifications, appropriate pharmacy
            arrangements, professional responsibilities, governance, commissioning and contractual approvals.
          </p>
          <button onClick={() => scrollToId("ooh-enquiry")} data-testid="ooh-partnership-cta"
            className="group mt-6 inline-flex items-center gap-2.5 bg-[#0B192C] text-white font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] hover:text-[#0B192C]">
            Enquire About Commissioning NCS Out of Hours
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>

    {/* REFERRER CONTACT */}
    <section className="pb-20 sm:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div
            data-testid="ooh-referrer-contact"
            className="relative overflow-hidden rounded-3xl bg-[#0B192C] px-8 py-10 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
          >
            <div className="relative lg:col-span-8 flex items-start gap-5">
              <span className="hidden sm:flex w-14 h-14 rounded-2xl bg-[#06B6D4]/15 text-[#06B6D4] items-center justify-center shrink-0">
                <MoonStar size={26} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">
                  For healthcare professionals & referrers
                </p>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  On call 9:00 pm – 8:00 am, every night
                </h2>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-white/60 max-w-xl">
                  GPs and healthcare professionals can email our team with out-of-hours medication
                  enquiries — the on-call coordinator picks up referrer emails during operating
                  hours. For commissioning and contract discussions, please use the enquiry form below.
                </p>
              </div>
            </div>
            <div className="relative lg:col-span-4 lg:text-right">
              <a
                href="mailto:yasin@nationalclinicalservices.co.uk"
                data-testid="ooh-referrer-email"
                className="group inline-flex items-center gap-2.5 bg-white text-[#0B192C] font-bold px-6 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] break-all"
              >
                <Mail size={16} className="text-[#06B6D4] shrink-0" />
                yasin@nationalclinicalservices.co.uk
              </a>
              <p className="mt-3 text-[11px] text-white/40">Referrer & service enquiries by email</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* ENQUIRY FORM */}
    <section id="ooh-enquiry" className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">ICB & NHS commissioner enquiries</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
            Discuss Your Out-of-Hours Service Requirements
          </h2>
          <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
            Are you an ICB, NHS organisation or healthcare provider interested in commissioning an
            out-of-hours medication supply and delivery service? Complete the form below and our team
            will contact you to discuss your requirements.
          </p>
        </Reveal>
        <Reveal>
          <OohEnquiryForm />
        </Reveal>
      </div>
    </section>
  </main>
);

export default OutOfHours;
